import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, ChevronDownIcon, ChevronUpDownIcon, ClipboardDocumentIcon, EyeIcon, EyeSlashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { useSnackbar } from '../../providers/SnackbarContext'; // Added import for useSnackbar
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'; // Added import
import GraphqlService from '../../service/graphqlService'; // Placeholder import - replace with actual path to GraphqlService
import { shouldShowIpWhitelist } from '../../utils/environmentConfig';
import usePermission from '../../authorization/permissionHook';
import { SHOW_POLICY_TEMPLATES } from '../../utils/constants';

const CREDENTIAL_REQUEST_DOCUMENTATION_URL = 'https://nestle.atlassian.net/wiki/spaces/DIIS/pages/615878194';

// A template is treated as "Certificate Provisioning" (exclusive selection, feature 024)
// when one of its Services entries is the CERTIFICATE_PROVISION service code, or its
// description mentions certificate provisioning. Matching is normalized (case/underscore
// insensitive) since the platform uses the enum-like code "CERTIFICATE_PROVISION" rather
// than the human-readable phrase.
const normalize = (value) => (typeof value === 'string' ? value.toLowerCase().replace(/[^a-z]/g, '') : '');
const isCertificateProvisioningTemplate = (template) => {
  const servicesMatch = (template?.services || []).some((service) => normalize(service).includes('certificateprovision'));
  const descriptionMatch = normalize(template?.description).includes('certificateprovision');
  return servicesMatch || descriptionMatch;
};

const LoadingSpinner = () => (
  <div className="flex justify-center items-center">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

const getUserNameFromLocalStorage = () => {
  // the username has the format: test.admin@es.nestle.com
  // take the part before the @ symbol
  const username = localStorage.getItem('username');
  if (username) {
    const atIndex = username.indexOf('@');
    const newUser = atIndex !== -1 ? username.substring(0, atIndex) : username;
    // delete symbols from the newUser like .
    return newUser.replace(/[^a-zA-Z0-9]/g, ''); // Remove all non-alphanumeric characters

  }
  return ''; // Return empty string if no username found
};

export default function CreateCredentialModal({
  isOpen,
  onClose,
  availableProjects: initialAvailableProjects, // Renamed prop
  projectsLoading, // Receive loading state
  projectsError,   // Receive error state
  onAdd, // Added onAdd prop
}) {
  const openSnackBar = useSnackbar();
  const [formData, setFormData] = useState({
    project: null, // Changed to a single project ID
    name: getUserNameFromLocalStorage(),
    description: '', // Added description field
    quota: '',
    alertThreshold: '',
    iPWhiteList: '',
    pointOfContact: '',
  });
  const [availableProjects, setAvailableProjects] = useState(initialAvailableProjects || []);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [ipError, setIpError] = useState('');
  const [pointOfContactError, setPointOfContactError] = useState('');
  const [quotaError, setQuotaError] = useState(''); // Added quotaError state
  const [alertThresholdError, setAlertThresholdError] = useState(''); // Added alertThresholdError state
  const [descriptionError, setDescriptionError] = useState(''); // Added descriptionError state
  const [showSuccessView, setShowSuccessView] = useState(false);
  const [createdCredentialName, setCreatedCredentialName] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Added isSubmitting state
  const [credentialDataForAudit, setCredentialDataForAudit] = useState(null); // Store data to send to onAdd after success view is closed
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]); // Feature 024 - selected API templates
  const [templateSearch, setTemplateSearch] = useState('');
  const [, canManagePolicyTemplates] = usePermission(SHOW_POLICY_TEMPLATES); // Feature 031 - cross-link hint

  const queryClient = useQueryClient(); // Added for react-query

  const {
    data: templates,
    isLoading: templatesLoading,
    isError: templatesError,
  } = useQuery(['permissionTemplates'], () => GraphqlService.getTemplates(), {
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const filteredTemplates = useMemo(() => {
    const list = templates || [];
    if (!templateSearch.trim()) return list;
    const search = templateSearch.trim().toLowerCase();
    return list.filter((template) => template.description?.toLowerCase().includes(search)
      || (template.services || []).some((service) => service.toLowerCase().includes(search)));
  }, [templates, templateSearch]);

  const hasCertificateProvisioningSelected = useMemo(() => {
    const selectedTemplates = (templates || []).filter((template) => selectedTemplateIds.includes(template.templateId));
    return selectedTemplates.some(isCertificateProvisioningTemplate);
  }, [templates, selectedTemplateIds]);

  const hasOtherSelected = useMemo(() => {
    const selectedTemplates = (templates || []).filter((template) => selectedTemplateIds.includes(template.templateId));
    return selectedTemplates.some((template) => !isCertificateProvisioningTemplate(template));
  }, [templates, selectedTemplateIds]);

  const handleToggleTemplate = (template) => {
    const isSelected = selectedTemplateIds.includes(template.templateId);
    if (isSelected) {
      setSelectedTemplateIds((prev) => prev.filter((id) => id !== template.templateId));
      return;
    }
    if (isCertificateProvisioningTemplate(template)) {
      // Certificate Provisioning cannot be combined with any other API - selecting it clears the rest.
      setSelectedTemplateIds([template.templateId]);
    } else {
      // Selecting a non-Certificate-Provisioning API drops Certificate Provisioning if it was selected.
      setSelectedTemplateIds((prev) => [
        ...prev.filter((id) => {
          const prevTemplate = (templates || []).find((t) => t.templateId === id);
          return prevTemplate && !isCertificateProvisioningTemplate(prevTemplate);
        }),
        template.templateId,
      ]);
    }
  };

  const mutation = useMutation({
    mutationFn: (newCredential) => GraphqlService.createNewCredential(newCredential),
    onSuccess: (data) => {
      // Credential created successfully - show success view with keys
      setCreatedCredentialName(formData.name);
      setAccessKey(data.accessKey || '');
      setSecretKey(data.secretAccessKey || '');
      setShowSuccessView(true);

      // Store credential data to send to parent AFTER user closes success view
      // This prevents the parent from closing the modal before success view is displayed
      const credentialData = {
        id: data.id || data.userId || formData.name,
        userId: data.userId || data.id,
        name: formData.name,
        description: formData.description,
        quota: formData.quota,
        alertThreshold: formData.alertThreshold,
        pointOfContact: formData.pointOfContact,
        iPWhiteList: formData.iPWhiteList.split(',').map(ip => ip.trim()).filter(ip => ip !== ''),
        projects: [formData.project],
        isNewCredential: true // Flag to indicate this is a new credential (prevents individual field logs)
      };
      setCredentialDataForAudit(credentialData);

      // Invalidate the query used in the parent component
      queryClient.invalidateQueries(['physicalUsers']);

      openSnackBar('Credential created successfully!', 'success');
    },
    onError: (error) => {
      openSnackBar(`Error creating credential: ${error.message}`, 'error');
    },
  });

  useEffect(() => {
    setAvailableProjects(initialAvailableProjects || []);
  }, [initialAvailableProjects]);

  // Asegurarse de que el nombre siempre esté sincronizado con localStorage y correctamente formateado
  useEffect(() => {
    const formattedUsername = getUserNameFromLocalStorage();
    if (formattedUsername && formData.name !== formattedUsername) {
      setFormData(prev => ({ ...prev, name: formattedUsername }));
    }
  }, [isOpen, formData.name]); // Se ejecuta cuando el modal se abre o cuando cambia el nombre

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'iPWhiteList') {
      if (value.trim() === '') {
        setIpError('');
        return;
      }
      const ips = value.split(',').map(ip => ip.trim());
      const ipRegex = /^([0-9]{1,3}\.){3}[0-9]{1,3}$/; // Updated regex to allow 0-999 in each octet
      const invalidIPs = ips.filter(ip => ip !== '' && !ipRegex.test(ip));
      if (invalidIPs.length > 0) {
        setIpError(`Invalid IP addresses: ${invalidIPs.join(', ')}. Please use comma-separated valid IPs.`);
      } else {
        setIpError('');
      }
    } else if (name === 'pointOfContact') {
      if (value.trim() === '') {
        setPointOfContactError('');
        return;
      }
      const emails = value.split(',').map(email => email.trim());
      // Basic email regex, consider a more robust one for production
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter(email => email !== '' && !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        setPointOfContactError(`Invalid email addresses: ${invalidEmails.join(', ')}. Please use comma-separated valid emails.`);
      } else {
        setPointOfContactError('');
      }
    } else if (name === 'quota') {
      if (value.trim() !== '' && parseInt(value, 10) <= 0) {
        setQuotaError('Quota must be greater than 0.');
      } else {
        setQuotaError('');
      }
    } else if (name === 'alertThreshold') {
      if (value.trim() !== '' && parseInt(value, 10) < 0) {
        setAlertThresholdError('Alert Threshold cannot be negative.');
      } else {
        setAlertThresholdError('');
      }
    } else if (name === 'description') {
      if (value.trim() === '') {
        setDescriptionError('Description is required.');
      } else {
        setDescriptionError('');
      }
    }
  };

  const handleProjectSelect = (projectId) => {
    setFormData((prev) => ({
      ...prev,
      project: prev.project === projectId ? null : projectId, // Toggle selection
    }));
    setIsProjectDropdownOpen(false); // Close dropdown after selection
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.project) {
      openSnackBar('Please select a project.', 'error');
      return;
    }
    if (!formData.name.trim()) {
      openSnackBar('Please enter a credential name.', 'error');
      return;
    }
    if (!formData.description.trim()) {
      setDescriptionError('Description is required.');
      openSnackBar('Please enter a description.', 'error');
      return;
    }
    if (selectedTemplateIds.length === 0) {
      openSnackBar('Please select at least one API.', 'error');
      return;
    }
    // iPWhiteList is only required in prod
    const relevantErrors = [quotaError, alertThresholdError, pointOfContactError, descriptionError];
    if (shouldShowIpWhitelist()) {
      relevantErrors.push(ipError);
    }
    if (relevantErrors.some(error => error)) {
      openSnackBar('Please correct the errors in the form.', 'error');
      return;
    }

    setIsSubmitting(true);

    const submissionData = {
      projects: [formData.project],
      name: formData.name,
      description: formData.description,
      quota: formData.quota,
      alertThreshold: formData.alertThreshold,
      iPWhiteList: formData.iPWhiteList.split(',').map(ip => ip.trim()).filter(ip => ip !== ''),
      pointOfContact: formData.pointOfContact,
      templateIds: selectedTemplateIds,
    };

    mutation.mutate(submissionData, {
      onSettled: () => setIsSubmitting(false),
    });
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Optional: Show a success message or change icon
      openSnackBar('Copied to clipboard!', 'success');
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      openSnackBar('Failed to copy text to clipboard', 'error');
    });
  };

  const handleDownloadCsv = () => {
    const csvContent = `AccessKey,SecretKey\n${accessKey},${secretKey}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${createdCredentialName}_credentials.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      openSnackBar("Failed to download CSV. Your browser might not support this feature.", 'error');
    }
  };

  const handleCloseSuccessView = () => {
    setShowSuccessView(false);
    
    // Call onAdd with credential data BEFORE closing modal
    // This allows the parent to handle audit logging before the modal is removed
    if (credentialDataForAudit && typeof onAdd === 'function') {
      onAdd(credentialDataForAudit);
    }
    
    // Reset form or redirect as needed
    setFormData({
      project: null,
      name: getUserNameFromLocalStorage(), // Use the same function that handles username formatting
      description: '',
      quota: '',
      alertThreshold: '',
      iPWhiteList: '',
      pointOfContact: '',
    });
    // Reset all error states
    setIpError('');
    setPointOfContactError('');
    setQuotaError('');
    setAlertThresholdError('');
    setDescriptionError('');

    setCreatedCredentialName('');
    setAccessKey('');
    setSecretKey('');
    setShowSecretKey(false);
    setCredentialDataForAudit(null); // Clear stored data
    setSelectedTemplateIds([]);
    setTemplateSearch('');
    onClose(); // Close the modal
  };

  // Don't return null if showSuccessView is true - we need to display the success popup
  if (!isOpen && !showSuccessView) return null;

  if (isSubmitting) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-700">Creating credential, please wait...</p>
        </div>
      </div>
    );
  }

  if (showSuccessView) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="p-6 text-center">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Create Credential</h3>
            <p className="text-sm text-gray-600 mb-4">
              Your new Credential <span className="font-bold">{createdCredentialName}</span> has been created successfully!
            </p>
            <p className="text-sm text-gray-600 mb-6">
              Find below the keys for this Credential. Store them somewhere safe as you can&apos;t
              view them again after closing this screen.
            </p>

            <div className="mb-4">
              <label htmlFor="accessKey" className="block text-sm font-medium text-gray-700 text-left mb-1">Access Key</label>
              <div className="relative">
                <input
                  id="accessKey"
                  type="text"
                  value={accessKey}
                  readOnly
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 pr-10"
                />
                <button
                  onClick={() => handleCopyToClipboard(accessKey)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-blue-500"
                  aria-label="Copy Access Key"
                >
                  <ClipboardDocumentIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="secretKey" className="block text-sm font-medium text-gray-700 text-left mb-1">Secret Access Key</label>
              <div className="relative">
                <input
                  id="secretKey"
                  type={showSecretKey ? 'text' : 'password'}
                  value={secretKey}
                  readOnly
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 pr-20" // Increased padding for two icons
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  <button
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    className="px-3 text-gray-500 hover:text-blue-500"
                    aria-label={showSecretKey ? "Hide Secret Key" : "Show Secret Key"}
                  >
                    {showSecretKey ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                  <button
                    onClick={() => handleCopyToClipboard(secretKey)}
                    className="px-3 text-gray-500 hover:text-blue-500 border-l border-gray-200"
                    aria-label="Copy Secret Key"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={handleDownloadCsv}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                Download .csv file
              </button>
              <button
                onClick={handleCloseSuccessView}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                I&apos;m ready to close this screen
              </button>
            </div>
            <div className="text-center mt-4">
              <a
                href={CREDENTIAL_REQUEST_DOCUMENTATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline"
              >
                View documentation
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800">Create Credential</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Modal Body - Scrollable */}
        <form onSubmit={handleSubmit} id="credentialForm" className="p-6 space-y-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
          <div>
            <label id="project-label" htmlFor="projects" className="block text-sm font-medium text-gray-700 mb-1">
              Project <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <button
                type="button"
                name="projects"
                id="projects"
                onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                aria-haspopup="listbox"
                aria-expanded={isProjectDropdownOpen}
                aria-labelledby="project-label projects"
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pr-10 text-left appearance-none"
              >
                {formData.project ? (availableProjects.find(p => p.value === formData.project)?.label || 'Select Project') : 'Select Project'}
              </button>
              <ChevronDownIcon className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              {isProjectDropdownOpen && (
                <ul
                  className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-auto shadow-lg"
                  role="listbox"
                  id="project-listbox"
                  aria-labelledby="project-label"
                >
                  {projectsLoading && <li className="p-2 text-gray-500">Loading projects...</li>}
                  {projectsError && <li className="p-2 text-red-500">Error loading projects.</li>}
                  {!projectsLoading && !projectsError && availableProjects && availableProjects.length > 0 ? (
                    availableProjects.map((project) => (
                      <li
                        key={project.value}
                        role="option"
                        aria-selected={formData.project === project.value}
                      >
                        <button
                          type="button"
                          onClick={() => handleProjectSelect(project.value)}
                          className={`w-full text-left p-2 hover:bg-gray-100 cursor-pointer ${formData.project === project.value ? 'bg-blue-50' : ''}`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleProjectSelect(project.value);
                            }
                          }}
                        >
                          {project.label}
                          {formData.project === project.value && <span className="ml-2 text-blue-500">✓</span>}
                        </button>
                      </li>
                    ))
                  ) : (
                    !projectsLoading && !projectsError && <li className="p-2 text-gray-500">No projects available</li>
                  )}
                </ul>
              )}
            </div>
          </div>

          <div>
            {/* El nombre del usuario ya no es editable, se utiliza automáticamente el valor de localStorage */}
            <input
              type="hidden"
              name="name"
              id="name"
              value={formData.name}
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              id="description"
              rows="3"
              value={formData.description}
              onChange={handleChange}
              className={`w-full p-2 border ${descriptionError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`}
              placeholder="Enter a description for this credential"
              required
            ></textarea>
            {descriptionError && <p className="text-red-500 text-xs mt-1">{descriptionError}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              APIs <span className="text-red-500">*</span>
            </label>
            {templatesLoading && <p className="text-sm text-gray-500">Loading available APIs...</p>}
            {templatesError && <p className="text-sm text-red-500">Error loading available APIs. Please try again.</p>}
            {!templatesLoading && !templatesError && (templates || []).length === 0 && (
              <p className="text-sm text-gray-500">No APIs available.</p>
            )}
            {!templatesLoading && !templatesError && (templates || []).length > 0 && (
              <>
                {(templates || []).length > 5 && (
                  <input
                    type="text"
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    placeholder="Search APIs..."
                    className="w-full p-2 mb-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                )}
                <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {filteredTemplates.map((template) => {
                    const isSelected = selectedTemplateIds.includes(template.templateId);
                    const isCertProvisioning = isCertificateProvisioningTemplate(template);
                    const isDisabled = (isCertProvisioning && hasOtherSelected && !isSelected)
                      || (!isCertProvisioning && hasCertificateProvisioningSelected && !isSelected);
                    return (
                      <label
                        key={template.templateId}
                        className={`flex items-start gap-2 p-2 text-sm ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => handleToggleTemplate(template)}
                          className="mt-1"
                        />
                        <span>
                          <span className="font-medium text-gray-800">{template.description || template.templateId}</span>
                          {template.services && template.services.length > 0 && (
                            <span className="block text-xs text-gray-500">{template.services.join(', ')}</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {hasCertificateProvisioningSelected && (
                  <p className="text-xs text-amber-700 mt-1">Certificate Provisioning cannot be combined with other APIs.</p>
                )}
              </>
            )}
            {canManagePolicyTemplates && (
              <p className="text-xs text-gray-400 mt-1">
                Don&apos;t see the API you need? <a href="#/policyTemplates" className="text-indigo-600 hover:text-indigo-800">Manage Policy Templates</a>.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="quota" className="block text-sm font-medium text-gray-700 mb-1">
                Quota <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="quota"
                  id="quota"
                  value={formData.quota}
                  onChange={handleChange}
                  placeholder="Input your initial Certificate Quota"
                  className={`w-full p-2 border ${quotaError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pr-10`}
                  required
                />
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              </div>
              {quotaError && <p className="text-red-500 text-xs mt-1">{quotaError}</p>}
            </div>
            <div>
              <label htmlFor="alertThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                Alert Threshold <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="alertThreshold"
                  id="alertThreshold"
                  min="0"
                  value={formData.alertThreshold}
                  onChange={handleChange}
                  placeholder="Receive an alert when quota reaches this value"
                  className={`w-full p-2 border ${alertThresholdError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pr-10`}
                  required
                />
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              </div>
              {alertThresholdError && <p className="text-red-500 text-xs mt-1">{alertThresholdError}</p>}
            </div>
          </div>

          {shouldShowIpWhitelist() && (
            <div>
              <label htmlFor="iPWhiteList" className="block text-sm font-medium text-gray-700 mb-1">
                Whitelisted IPs <span className="text-red-500">*</span>
              </label>
              <textarea
                name="iPWhiteList"
                id="iPWhiteList"
                value={formData.iPWhiteList}
                onChange={handleChange}
                placeholder="Please, insert a comma separated list"
                rows="3"
                className={`w-full p-2 border ${ipError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`}
                required
              ></textarea>
              {ipError && <p className="text-red-500 text-xs mt-1">{ipError}</p>}
            </div>
          )}

          <div>
            <label htmlFor="pointOfContact" className="block text-sm font-medium text-gray-700 mb-1">
              Points of contact <span className="text-red-500">*</span>
            </label>
            <textarea
              name="pointOfContact"
              id="pointOfContact"
              value={formData.pointOfContact}
              onChange={handleChange}
              placeholder="Please insert email addresses separated by comma"
              rows="3"
              className={`w-full p-2 border ${pointOfContactError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`}
              required
            ></textarea>
            {pointOfContactError && <p className="text-red-500 text-xs mt-1">{pointOfContactError}</p>}
          </div>
        </form>

        {/* Modal Footer */}
        <div className="flex justify-end items-center p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="credentialForm"
            disabled={isSubmitting}
            className="ml-3 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isSubmitting ? <LoadingSpinner /> : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
